/**
 * aiSSEService — fetch-based SSE client for AI generation endpoints.
 *
 * Uses the Fetch ReadableStream API (not EventSource) so we can:
 * - Send a POST body with the generation parameters
 * - Include the Authorization header
 *
 * Every event emitted by the backend is a `data: {json}\n\n` line.
 * The caller supplies an `onEvent` callback that receives typed SSEEvent objects.
 */

import type {
  GenerateLessonPlanData,
  GenerateSlidesData,
  GenerateTeachingNotesData,
  RefineLessonPlanData,
  RefineStageData,
  SSEEvent,
} from '../types';

const TOKEN_KEY = 'lessoncraft_token';
const API_BASE = import.meta.env.VITE_API_URL ?? '/api';

type OnEvent = (event: SSEEvent) => void;
type OnComplete = () => void;
type OnError = (err: Error) => void;

interface SSESubscription {
  /** Abort the stream before it naturally completes. */
  abort: () => void;
}

/**
 * Streams an SSE endpoint. Returns a subscription with an abort() method.
 * The stream is consumed until the server closes the connection.
 */
async function streamSSE(
  path: string,
  body: unknown,
  onEvent: OnEvent,
  onComplete: OnComplete,
  onError: OnError
): Promise<SSESubscription> {
  const controller = new AbortController();

  const token = localStorage.getItem(TOKEN_KEY);
  const headers: HeadersInit = { 'Content-Type': 'application/json' };
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  const doFetch = async () => {
    let response: Response;
    try {
      response = await fetch(`${API_BASE}${path}`, {
        method: 'POST',
        headers,
        body: JSON.stringify(body),
        signal: controller.signal,
      });
    } catch (err) {
      if ((err as Error).name === 'AbortError') return;
      onError(err instanceof Error ? err : new Error(String(err)));
      return;
    }

    if (!response.ok) {
      onError(new Error(`HTTP ${response.status}: ${response.statusText}`));
      return;
    }

    if (!response.body) {
      onError(new Error('Response body is null'));
      return;
    }

    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';

    try {
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });

        // SSE frames are separated by double newlines.
        const frames = buffer.split('\n\n');
        // The last element may be an incomplete frame — keep it in the buffer.
        buffer = frames.pop() ?? '';

        for (const frame of frames) {
          const dataLine = frame
            .split('\n')
            .find((line) => line.startsWith('data: '));
          if (!dataLine) continue;

          const jsonStr = dataLine.slice(6).trim();
          try {
            const event = JSON.parse(jsonStr) as SSEEvent;
            onEvent(event);
          } catch {
            // Malformed JSON — skip the frame silently.
          }
        }
      }
    } catch (err) {
      if ((err as Error).name === 'AbortError') return;
      onError(err instanceof Error ? err : new Error(String(err)));
      return;
    }

    onComplete();
  };

  // Start the fetch without blocking the caller.
  void doFetch();

  return { abort: () => controller.abort() };
}

// ─── Public API ───────────────────────────────────────────────────────────────

export interface SSECallbacks {
  onEvent: OnEvent;
  onComplete?: OnComplete;
  onError?: OnError;
}

const noop = () => undefined;

export const aiSSEService = {
  generateLessonPlan(
    data: GenerateLessonPlanData,
    callbacks: SSECallbacks
  ): Promise<SSESubscription> {
    return streamSSE(
      '/ai/generate-lesson-plan',
      data,
      callbacks.onEvent,
      callbacks.onComplete ?? noop,
      callbacks.onError ?? noop
    );
  },

  refineLessonPlan(
    data: RefineLessonPlanData,
    callbacks: SSECallbacks
  ): Promise<SSESubscription> {
    return streamSSE(
      '/ai/refine-lesson-plan',
      data,
      callbacks.onEvent,
      callbacks.onComplete ?? noop,
      callbacks.onError ?? noop
    );
  },

  generateSlides(
    data: GenerateSlidesData,
    callbacks: SSECallbacks
  ): Promise<SSESubscription> {
    return streamSSE(
      '/ai/slides',
      data,
      callbacks.onEvent,
      callbacks.onComplete ?? noop,
      callbacks.onError ?? noop
    );
  },

  refineStage(
    data: RefineStageData,
    callbacks: SSECallbacks
  ): Promise<SSESubscription> {
    return streamSSE(
      '/ai/refine-stage',
      data,
      callbacks.onEvent,
      callbacks.onComplete ?? noop,
      callbacks.onError ?? noop
    );
  },

  generateTeachingNotes(
    data: GenerateTeachingNotesData,
    callbacks: SSECallbacks
  ): Promise<SSESubscription> {
    return streamSSE(
      '/ai/generate-teaching-notes',
      data,
      callbacks.onEvent,
      callbacks.onComplete ?? noop,
      callbacks.onError ?? noop
    );
  },
};
